// Copyright (c) 2026 Goffy. All Rights Reserved.

// Client-side performance metrics, flushed as one batched `metric.emit`
// WebSocket frame per interval. Recording is O(1) per event with no
// JSON.stringify anywhere on the hot path; the module never sends per-event
// frames — that per-message overhead is exactly what these metrics exist to
// diagnose. This module must not import bffWebSocket (the transport is
// injected via setMetricsTransport to avoid a cycle).

import { AppState, Platform } from 'react-native';

export type MetricTags = Readonly<Record<string, string>>;

type InstrumentKind = 'counter' | 'histogram';

type InstrumentSpec = {
  kind: InstrumentKind;
  // Tag keys accepted for this instrument, in fixed order for series keys.
  tagKeys: readonly string[];
};

type SeriesState = {
  name: string;
  kind: InstrumentKind;
  tags: Record<string, string> | undefined;
  // counter
  value: number;
  // histogram (raw samples, ms for durations; BFF converts to seconds)
  samples: number[];
};

export type MetricEmitFrame = {
  service: 'Frontend-app';
  timestamp: string;
  meta: { platform: string; environment: string };
  metrics: Array<{
    name: string;
    kind: InstrumentKind;
    value?: number;
    values?: number[];
    tags?: Record<string, string>;
  }>;
};

// Wire names must match the FrontendMetricsRecorder allowlist in the BFF.
const INSTRUMENTS: Record<string, InstrumentSpec> = {
  'goffy.frontend.jsthread.blocked.duration': { kind: 'histogram', tagKeys: [] },
  'goffy.frontend.ws.message.processing.duration': { kind: 'histogram', tagKeys: ['stage', 'messageType', 'socket'] },
  'goffy.frontend.ws.message.bytes': { kind: 'histogram', tagKeys: ['messageType', 'socket'] },
  'goffy.frontend.ws.messages.received': { kind: 'counter', tagKeys: ['messageType', 'socket'] },
  'goffy.frontend.ws.messages.sent': { kind: 'counter', tagKeys: ['messageType', 'socket', 'outcome'] },
  'goffy.frontend.ws.outbound.queue.depth': { kind: 'histogram', tagKeys: ['socket'] },
  'goffy.frontend.ws.reconnect.count': { kind: 'counter', tagKeys: ['socket'] },
  'goffy.frontend.score.roundtrip.duration': { kind: 'histogram', tagKeys: ['messageType'] },
  'goffy.frontend.score.input.commit.duration': { kind: 'histogram', tagKeys: ['method'] },
  'goffy.frontend.score.input.count': { kind: 'counter', tagKeys: ['method'] },
  'goffy.frontend.hole.selector.duration': { kind: 'histogram', tagKeys: ['selector'] },
  'goffy.frontend.metric.dropped': { kind: 'counter', tagKeys: ['reason'] },
  'goffy.frontend.ws.request.outcome': { kind: 'counter', tagKeys: ['messageType', 'socket', 'outcome'] },
  'goffy.frontend.ws.request.reply.duration': { kind: 'histogram', tagKeys: ['messageType'] },
  'goffy.frontend.ws.queue.cleared': { kind: 'counter', tagKeys: ['reason'] },
  'goffy.frontend.register.count': { kind: 'counter', tagKeys: ['outcome', 'reason'] },
  // Positioning quality. The sbas/l5 tags are what make an accuracy number
  // interpretable — see services/gnssQuality.
  'goffy.frontend.gnss.fix.accuracy': { kind: 'histogram', tagKeys: ['sbas', 'l5', 'averaged'] },
  'goffy.frontend.gnss.satellites.used': { kind: 'histogram', tagKeys: ['sbas', 'l5'] },
  'goffy.frontend.gnss.snapshot.missing': { kind: 'counter', tagKeys: ['reason'] },
};

// Known protocol message types; anything else becomes 'other' so the
// messageType tag stays bounded even if the server sends something new.
const KNOWN_MESSAGE_TYPES = new Set([
  'ping', 'pong', 'error',
  'score.update', 'hole.update', 'hole.info', 'hole.view', 'hole.event',
  'game.result', 'round.leaderboard', 'tour.leaderboard.update', 'tour.leaderboard.state',
  'round.state', 'round.join', 'round.leave', 'round.requestState',
  'round.created', 'round.updated', 'round.removed', 'round.status.updated',
  'round.groups.updated', 'round.groups.update.rejected', 'round.groups.swap.rejected',
  'round.subgames.state', 'round.nassau.press.create',
  'tour.join', 'tour.leave', 'tour.created', 'tour.updated', 'tour.invite.request',
  'tour.members.snapshot', 'tour.member.claim', 'tour.member.claim.release',
  'tour.leaderboard.request', 'players_snapshot', 'players_error', 'team.created',
  'player.profile.update', 'player.update.ack', 'player.update.error', 'player.image.presign',
  'friend.list.snapshot', 'coachMark.state', 'coachMark.shown', 'coachMark.dismiss',
  'coachMark.preferences.set', 'coachMark.reset', 'user.create',
  'log.emit', 'metric.emit', 'service.health', 'ws.timeout', 'ws.connect.timeout',
  'telemetry.battery', 'telemetry.battery.ack',
  // Outbound request types (requestId-carrying) — needed so the
  // ws.request.outcome/reply.duration tag never collapses to 'other'.
  'user.password.change', 'user.view',
  'tour.freeze.lock', 'tour.initiate', 'tour.update', 'tour.member.add', 'tour.member.remove',
  'tour.members.request', 'tour.image.presign', 'tour.photo.presign',
  'round.initiate', 'round.update', 'round.remove', 'round.member.add', 'round.member.remove',
  'round.status.update', 'round.groups.update', 'round.groups.swap', 'round.subgames.update',
  'friend.list.request', 'friend.add.request', 'friend.remove.request', 'friend.update.request',
  'friend.invite.request', 'friend.invite.redeem',
  'coachMark.state.request', 'bug.report', 'bug.image.presign',
  'player.temporary.create', 'golfCourse.upsert',
]);

const MAX_SERIES = 300;
const MAX_SAMPLES_PER_SERIES = 200;
const MAX_EVENTS_BEFORE_PRESSURE_FLUSH = 500;
// Keep above the BFF's 5 s per-connection rate limit with margin for jitter.
const MIN_FLUSH_GAP_MS = 6000;
const STALE_WINDOW_MS = 5 * 60 * 1000;
const DEFAULT_FLUSH_MS = 15000;

export const metricsEnabled = process.env.EXPO_PUBLIC_METRICS_DISABLED !== 'true';

const flushIntervalMs = (() => {
  const parsed = Number(process.env.EXPO_PUBLIC_METRICS_FLUSH_MS);
  return Number.isFinite(parsed) && parsed >= MIN_FLUSH_GAP_MS ? parsed : DEFAULT_FLUSH_MS;
})();

export const nowMs: () => number =
  typeof globalThis.performance?.now === 'function'
    ? () => globalThis.performance.now()
    : () => Date.now();

const series = new Map<string, SeriesState>();
let eventsSinceFlush = 0;
let windowStartedAtWallClock = Date.now();
let lastFlushAttemptAt = 0;
let transport: ((frame: MetricEmitFrame) => boolean) | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let lifecycleHooked = false;

function seriesKey(name: string, spec: InstrumentSpec, tags: MetricTags | undefined): string {
  if (!tags || spec.tagKeys.length === 0) {
    return name;
  }
  let key = name;
  for (const tagKey of spec.tagKeys) {
    const value = tags[tagKey];
    if (value !== undefined) {
      key += `|${tagKey}=${value}`;
    }
  }
  return key;
}

function pickTags(spec: InstrumentSpec, tags: MetricTags | undefined): Record<string, string> | undefined {
  if (!tags || spec.tagKeys.length === 0) {
    return undefined;
  }
  let picked: Record<string, string> | undefined;
  for (const tagKey of spec.tagKeys) {
    const value = tags[tagKey];
    if (value !== undefined) {
      (picked ??= {})[tagKey] = value;
    }
  }
  return picked;
}

function getSeries(name: string, tags: MetricTags | undefined): SeriesState | null {
  const spec = INSTRUMENTS[name];
  if (!spec) {
    if (__DEV__) {
      console.warn(`[appMetrics] unknown instrument dropped: ${name}`);
    }
    return null;
  }
  const key = seriesKey(name, spec, tags);
  let state = series.get(key);
  if (!state) {
    if (series.size >= MAX_SERIES) {
      countDrop('series_cap');
      return null;
    }
    state = { name, kind: spec.kind, tags: pickTags(spec, tags), value: 0, samples: [] };
    series.set(key, state);
  }
  return state;
}

// Bypasses caps so drops are always visible.
function countDrop(reason: 'sample_cap' | 'series_cap' | 'stale_window') {
  const name = 'goffy.frontend.metric.dropped';
  const key = `${name}|reason=${reason}`;
  let state = series.get(key);
  if (!state) {
    state = { name, kind: 'counter', tags: { reason }, value: 0, samples: [] };
    series.set(key, state);
  }
  state.value += 1;
}

export function normalizeMessageType(type: string | undefined): string {
  return type !== undefined && KNOWN_MESSAGE_TYPES.has(type) ? type : 'other';
}

export function incrementCounter(name: string, value = 1, tags?: MetricTags): void {
  if (!metricsEnabled || value <= 0) return;
  const state = getSeries(name, tags);
  if (!state || state.kind !== 'counter') return;
  state.value += value;
  eventsSinceFlush += 1;
  maybePressureFlush();
}

export function recordHistogram(name: string, value: number, tags?: MetricTags): void {
  if (!metricsEnabled || !Number.isFinite(value) || value < 0) return;
  const state = getSeries(name, tags);
  if (!state || state.kind !== 'histogram') return;
  if (state.samples.length >= MAX_SAMPLES_PER_SERIES) {
    countDrop('sample_cap');
    return;
  }
  state.samples.push(value);
  eventsSinceFlush += 1;
  maybePressureFlush();
}

export function timeSync<T>(name: string, tags: MetricTags | undefined, fn: () => T): T {
  if (!metricsEnabled) return fn();
  const start = nowMs();
  try {
    return fn();
  } finally {
    recordHistogram(name, nowMs() - start, tags);
  }
}

export function setMetricsTransport(sendFrame: (frame: MetricEmitFrame) => boolean): void {
  transport = sendFrame;
}

function maybePressureFlush() {
  if (eventsSinceFlush >= MAX_EVENTS_BEFORE_PRESSURE_FLUSH) {
    flushMetricsNow('pressure');
  }
}

export function flushMetricsNow(reason: 'interval' | 'background' | 'pressure' | 'manual'): void {
  if (!metricsEnabled || series.size === 0) {
    return;
  }

  const wallNow = Date.now();
  if (reason === 'pressure' && wallNow - lastFlushAttemptAt < MIN_FLUSH_GAP_MS) {
    return;
  }

  if (transport === null || wallNow - lastFlushAttemptAt < MIN_FLUSH_GAP_MS) {
    // No transport yet (pre-login) or too soon: retain the window, but drop
    // everything if it has gone stale to bound memory.
    if (wallNow - windowStartedAtWallClock > STALE_WINDOW_MS) {
      series.clear();
      eventsSinceFlush = 0;
      windowStartedAtWallClock = wallNow;
      countDrop('stale_window');
    }
    return;
  }

  lastFlushAttemptAt = wallNow;

  const frame: MetricEmitFrame = {
    service: 'Frontend-app',
    timestamp: new Date(wallNow).toISOString(),
    meta: {
      platform: Platform.OS,
      environment: process.env.EXPO_PUBLIC_APP_ENV ?? 'unknown',
    },
    metrics: [],
  };

  for (const state of series.values()) {
    if (state.kind === 'counter') {
      if (state.value > 0) {
        frame.metrics.push({ name: state.name, kind: 'counter', value: state.value, tags: state.tags });
      }
    } else if (state.samples.length > 0) {
      frame.metrics.push({ name: state.name, kind: 'histogram', values: state.samples, tags: state.tags });
    }
  }

  if (frame.metrics.length === 0) {
    return;
  }

  const sent = transport(frame);
  if (sent) {
    series.clear();
    eventsSinceFlush = 0;
    windowStartedAtWallClock = wallNow;
  } else if (wallNow - windowStartedAtWallClock > STALE_WINDOW_MS) {
    series.clear();
    eventsSinceFlush = 0;
    windowStartedAtWallClock = wallNow;
    countDrop('stale_window');
  }
}

export function startMetricsFlushLoop(): void {
  if (!metricsEnabled || flushTimer !== null) {
    return;
  }

  flushTimer = setInterval(() => flushMetricsNow('interval'), flushIntervalMs);

  if (lifecycleHooked) {
    return;
  }
  lifecycleHooked = true;

  if (Platform.OS === 'web') {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          flushMetricsNow('background');
        }
      });
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => flushMetricsNow('background'));
    }
  } else {
    AppState.addEventListener('change', (state) => {
      if (state !== 'active') {
        flushMetricsNow('background');
      }
    });
  }
}

export const __testOnly = {
  resetForTest(): void {
    series.clear();
    eventsSinceFlush = 0;
    windowStartedAtWallClock = Date.now();
    lastFlushAttemptAt = 0;
    transport = null;
    if (flushTimer !== null) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
  },
  snapshotForTest(): SeriesState[] {
    return [...series.values()].map((state) => ({ ...state, samples: [...state.samples] }));
  },
};
